import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { DimeAccordionContent, DimeAccordionTrigger } from './DimeAccordion.js';
import { DimeButton } from './DimeButton.js';
import {
  DimeItem,
  DimeItemActions,
  DimeItemContent,
  DimeItemFooter,
  DimeItemHeader,
  DimeItemMedia,
} from './DimeItem.js';

type DimeDetailItemActions =
  React.ReactNode | ((detailsTrigger: React.ReactNode) => React.ReactNode);

type DimeDetailItemProps = {
  chips?: React.ReactNode;
  media?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: DimeDetailItemActions;
  footer?: React.ReactNode;
  details?: React.ReactNode;
  detailsLabel?: string;
};

export function DimeDetailItem({
  chips,
  media,
  title,
  description,
  actions,
  footer,
  details,
  detailsLabel = 'Show details',
}: Readonly<DimeDetailItemProps>) {
  const detailsTrigger = details ? (
    <DimeAccordionTrigger asChild>
      <DimeButton type="button" variant="outline" size="icon-sm" aria-label={detailsLabel}>
        <ChevronDown className="in-data-[state=open]:rotate-180" />
      </DimeButton>
    </DimeAccordionTrigger>
  ) : null;
  const actionContent =
    typeof actions === 'function' ? (
      actions(detailsTrigger)
    ) : actions || detailsTrigger ? (
      <>
        {actions}
        {detailsTrigger}
      </>
    ) : null;

  return (
    <DimeItem variant="outline" className="[&>[data-slot=accordion-content]]:w-full">
      <div className="flex w-full min-w-0 items-start gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {chips != null ? (
            <DimeItemHeader className="min-w-0 basis-auto justify-start flex-wrap">
              {chips}
            </DimeItemHeader>
          ) : null}
          <div className="flex min-w-0 items-center gap-2.5">
            {media ? <DimeItemMedia aria-hidden>{media}</DimeItemMedia> : null}
            <DimeItemContent className="min-w-0 overflow-hidden">
              {title}
              {description}
            </DimeItemContent>
          </div>
        </div>
        {actionContent ? (
          <DimeItemActions className="shrink-0">{actionContent}</DimeItemActions>
        ) : null}
      </div>
      {details ? (
        <DimeAccordionContent className="flex w-full flex-col gap-1 pt-2">
          {details}
        </DimeAccordionContent>
      ) : null}
      {footer ? <DimeItemFooter className="w-full">{footer}</DimeItemFooter> : null}
    </DimeItem>
  );
}
